import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getModelApprovalStatuses, requestModelApproval, reviewModelApproval } from '@/api/modelApproval.api';
import useWorkspacesStore from '@/state/workspaces.state';
import { useSettingsStore } from '@/state/settings.state';
import { useUserStore } from '@/state/user.state';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';
import { WorkspaceModelStatus } from '@/types/api/modelDefinition';
import type { ModelApprovalStatus } from '@/types/api/modelApproval';

const logger = log.getLogger('useModelApproval');

export const MODEL_APPROVAL_STATUSES_QUERY_KEY = 'modelApprovalStatuses';

export const useApprovalStatusForModel = (modelId: string | undefined): ModelApprovalStatus =>
  useWorkflowStore((state) => (modelId ? state.modelApprovalStatuses[modelId] : null) ?? null);

export const useModelApprovalStatuses = (modelIds: string[], enabled = true) => {
  const workspaceId = useWorkspacesStore((state) => state.activeWorkspace.workspaceId);
  const stableKey = [...modelIds].sort().join(',');

  return useQuery({
    queryKey: [MODEL_APPROVAL_STATUSES_QUERY_KEY, workspaceId, stableKey],
    queryFn: () => getModelApprovalStatuses(workspaceId, modelIds),
    enabled: enabled && modelIds.length > 0,
    staleTime: 30_000,
    select: (data) => data.statuses,
  });
};

export const useSubmitModelApprovalRequest = () => {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspacesStore((state) => state.activeWorkspace.workspaceId);

  return useMutation({
    mutationFn: (body: { modelId: string; providerId: string; modelName?: string }) =>
      requestModelApproval(workspaceId, body),
    onMutate: ({ modelId }) => {
      const { modelApprovalStatuses, setModelApprovalStatuses } = useWorkflowStore.getState();
      const previous = modelApprovalStatuses[modelId] ?? null;
      setModelApprovalStatuses({ ...modelApprovalStatuses, [modelId]: WorkspaceModelStatus.Pending });
      return { previous };
    },
    onError: (_err, { modelId }, context) => {
      const { modelApprovalStatuses, setModelApprovalStatuses } = useWorkflowStore.getState();
      if (context?.previous == null) {
        const { [modelId]: _, ...rest } = modelApprovalStatuses;
        setModelApprovalStatuses(rest);
      } else {
        setModelApprovalStatuses({ ...modelApprovalStatuses, [modelId]: context.previous });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [MODEL_APPROVAL_STATUSES_QUERY_KEY] });
    },
  });
};

export const useReviewModelApproval = () => {
  const { t } = useTranslation();
  const workspaceId = useWorkspacesStore((state) => state.activeWorkspace.workspaceId);
  const { updateSnackbarData } = useGlobalStore();

  return useMutation({
    mutationFn: (body: { modelId: string; status: WorkspaceModelStatus.Approved | WorkspaceModelStatus.Rejected }) =>
      reviewModelApproval(workspaceId, body),
    onSuccess: (_data, { modelId, status }) => {
      const now = new Date().toISOString();
      const reviewerName = useUserStore.getState().user?.displayName ?? undefined;
      useSettingsStore.setState((state) => ({
        workspaceModels: state.workspaceModels.map((m) =>
          m.modelId === modelId
            ? { ...m, status, isAllowed: status === WorkspaceModelStatus.Approved, reviewerName, decidedAt: now }
            : m,
        ),
      }));
    },
    onError: (err) => {
      logger.error('Error reviewing model approval', err);
      updateSnackbarData({
        text: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.PENDING_REQUESTS.REVIEW_ERROR),
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    },
  });
};
